import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/observable/fromPromise';

import { OktaService } from './okta.service';
import { AuthEventService, authEventNames } from './auth.events.service';
import { AppUser, IdentitySession, UserRole } from '../model';
import { Utility } from '../../helpers/utility';

const _session = new BehaviorSubject<IdentitySession>(null);

@Injectable()
export class AuthenticationService {
    redirectUrl: string;

    private session = _session;
    private sessionChanges = this.session.asObservable().distinctUntilChanged();

    constructor(private okta: OktaService,
        private authEvent: AuthEventService) {

        authEvent.changes().subscribe((changes) => {
            if (changes && changes.event === authEventNames.AUTH_EVENT_TOKEN_REFRESHED) {
                this.onTokenRefreshed(changes.data);
            }
        });
    }

    getLoginWidget() {
        return this.okta.getWidget();
    }

    authenticate(idToken = null, accessToken = null): Observable<boolean> {

        if (idToken) {
            this.okta.addToken(idToken);
        }

        if (accessToken) {
            this.okta.addAccessToken(accessToken);
        }

        const pr = Utility.promiseFactory<boolean>();

        this.loadSessionAndToken().subscribe((result) => {
            if (result) {
                this.authEvent.raise(authEventNames.AUTH_EVENT_AUTHENTICATED);
                pr.resolve(true);
            } else {
                pr.resolve(false);
            }
        });

        return Observable.fromPromise(pr.promise);
    }

    refreshToken() {
        const pr = Utility.promiseFactory<boolean>();
        const token = this.okta.getToken();

        this.okta.refreshToken()
            .then((newToken) => {
                pr.resolve(true);
            })
            .catch((error) => {
                pr.reject(error);
            });
        return Observable.fromPromise(pr.promise);
    }

    isAuthenticated(): Observable<boolean> {
        const pr = Utility.promiseFactory<boolean>();
        const result = Observable.fromPromise(pr.promise);

        const currentSession = this.session.getValue();
        const token = this.okta.getToken();

        if (token) {
            pr.resolve(true);
            if (!(currentSession && currentSession.sessionToken && currentSession.jwtToken && currentSession.user)) {
                this.loadSessionAndToken();
            }
            return result;
        } else {
            this.loadSessionAndToken().subscribe((loadResult) => {
                pr.resolve(loadResult);
            });
        }
        return result;
    }

    logOut(): Observable<boolean> {
        const pr = Utility.promiseFactory<boolean>();

        this.okta.signOut(() => {
            this.session.next(null);
            this.okta.removeToken();
            pr.resolve(true);
            this.authEvent.raise(authEventNames.AUTH_EVENT_LOGOUT);
        });

        return Observable.fromPromise(pr.promise);
    }

    getCurrentUser(): AppUser {
        const currentSession = this.session.getValue();
        if (currentSession && currentSession.user) {
            return currentSession.user;
        }
        return null;
    }

    getIdToken(): any {
        const currentSession = this.session.getValue();
        if (currentSession && currentSession.jwtToken && currentSession.jwtToken.idToken) {
            return currentSession.jwtToken.idToken;
        }

        const token = this.okta.getToken();
        if (token) {
            return token.idToken;
        }

        return null;
    }

    getSessionChanges() {
        return this.sessionChanges;
    }

    getSessionValue() {
        return this.session.getValue();
    }

    private loadSessionAndToken(): Observable<boolean> {
        const pr = Utility.promiseFactory<boolean>();

        this.okta.checkSession().subscribe((session => {

            let hasAuthData = true;

            const token = this.okta.getToken();

            if (!token) {
                this.authEvent.raise(authEventNames.AUTH_EVENT_TOKEN_NOTFOUND);
                hasAuthData = false;
            }

            if (!hasAuthData) {
                this.updateIdentitySession();
                this.okta.removeToken();
                pr.resolve(false);
                return;
            }

            if (token) {
                this.authEvent.raise(authEventNames.AUTH_EVENT_SESSION_LOADED);
                this.updateIdentitySession(token, session);
                pr.resolve(true);
            }

        }));

        return Observable.fromPromise(pr.promise);

    }

    private updateIdentitySession(token = null, session = null) {

        if (!token && !session) {
            this.session.next(null);
            return;
        }

        let currentAuth = this.session.getValue();
        if (!currentAuth) {
            currentAuth = new IdentitySession();
        }

        if (session) {
            currentAuth.sessionToken = session;
        }

        if (token) {
            currentAuth.jwtToken = token;
            currentAuth.user = this.createAppUserFromToken(token);
        }

        const newSession = Object.assign({}, currentAuth, new IdentitySession());
        this.session.next(newSession);
    }

    private createAppUserFromToken(token): AppUser {

        if (token && token.claims) {
            return new AppUser({
                firstName: token.claims.UserFirstName,
                lastName: token.claims.UserLastName,
                email: token.claims.email,
                userId: token.claims.UserId,
                roles: this.convertRolesToUserRole(token.claims.UserRoles),
                assignedLabs: token.claims.AssignedLabs,
            });
        } else {
            throw new Error('Token has not been found to create AppUser');
        }
    }

    private convertRolesToUserRole(roleNames: string[]): UserRole[] {
        return roleNames.map(r => {
            return { roleName: r } as UserRole;
        });
    }

    private onTokenRefreshed(newToken) {
        if (newToken) {
            this.updateIdentitySession(newToken);
        }
    }
}





// WEBPACK FOOTER //
// /home/runner/work/cdg-missioncontrol/cdg-missioncontrol/MissionControlClient2/MissionControl.WebUi2/mc-app/src/app/core/security/services/authentication.service.ts